<script>
import { mapState, mapActions } from "vuex";

import NavigationWrapper from "@/components/NavigationWrapper";
import MultiSelect from "@/components/MultiSelect";
import { defaultHandle } from "@/utils";

export default {
  // Included components
  components: {
    NavigationWrapper,
    MultiSelect,
  },

  data() {
    return {
      // The group ID -- or null if the group is being created
      groupId: null,

      // The group being edited -- or null if it's being created
      group: {},

      // The name entered into the form
      name: "",

      // The group's handle
      handle: "",

      // Notes for the group
      notes: "",

      // Workers selected to be in the group
      selectedWorkers: [],

      // whether data is currently being fetched
      loadingState: true,
    };
  },

  computed: {
    // Whether the group is being edited (vs. created)
    editing() {
      return this.groupId != null;
    },

    // The workerIds of the selected workers
    selectedWorkerIds() {
      return this.selectedWorkers.map((worker) => worker.id);
    },

    // Mapped vuex state
    ...mapState(["groups", "workers"]),
  },

  // When the component is created, get the worker from the cache
  // and updated it from the API.
  // TODO: if the connection is slow and the user enters values before
  // the fetch finished, their values will be overridden.
  async created() {
    this.loadingState = true;
    this.groupId = this.$route.params.groupId;
    this.group = this.groups[this.groupId];

    if (this.editing) {
      // Get this current group
      await this.getGroup({ groupId: this.groupId });
      this.group = this.groups[this.groupId];

      // Set form fields
      this.name = this.group.name;
      this.notes = this.group.notes;
      this.handle = this.group.handle;
    }

    await this.getWorkers({ fields: "id,name" });

    if (this.editing) {
      let grp = this.group;
      // Get workers currently in group
      this.selectedWorkers = Object.values(this.workers).filter((worker) => {
        grp.hasOwnProperty("worker_ids") &&
          grp.worker_ids.indexOf(worker.id) !== -1;
      });
    }

    this.loadingState = false;
  },

  watch: {
    // When the name changes, update the handle to be the default value,
    // unless the handle has been manually changed to something different
    name(value, old) {
      if (this.handle === "" || this.handle === defaultHandle(old)) {
        this.handle = defaultHandle(value);
      }
    },
  },

  methods: {
    // When the form is submitted, either PUT or POST depending on
    // whether the form is being used to edit or create a group
    async submit() {
      if (this.editing) {
        this.put();
      } else {
        this.post();
      }
    },

    // Post a new group to the API from the form inputs
    async post() {
      // Add group given the form inputs
      const success = await this.postGroup({
        name: this.name,
        handle: this.handle,
        notes: this.notes,
        workerIds: this.selectedWorkerIds,
      });

      // If success, then redirect
      if (success) {
        await this.$router.push("/groups");
      }
    },

    // Update the existing group with the API
    async put() {
      // Update group given the form inputs
      const success = await this.putGroup({
        groupId: this.groupId,
        name: this.name,
        handle: this.handle,
        notes: this.notes,
        workerIds: this.selectedWorkerIds,
      });

      // If success, then redirect
      if (success) {
        await this.$router.push("/groups/" + this.groupId);
      }
    },

    // Return whether a given worker matches a given search string
    workerMatchesSearch(search, worker) {
      search = search.trim().toLowerCase();
      return worker.name.toLowerCase().includes(search);
    },

    // Mapped vuex actions
    ...mapActions(["getGroup", "putGroup", "postGroup", "getWorkers"]),
  },
};
</script>

<template>
  <NavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item to="/groups">Groups</b-breadcrumb-item>
      <b-breadcrumb-item v-if="editing" :to="`/groups/${groupId}`">
        {{ group.name }}
      </b-breadcrumb-item>
      <b-breadcrumb-item v-if="editing" active>Edit</b-breadcrumb-item>
      <b-breadcrumb-item v-else active>Add Group</b-breadcrumb-item>
    </template>
    <b-row class="mb-3">
      <b-col>
        <h1 v-if="editing">Edit Group</h1>
        <h1 v-else>Add Group</h1>
      </b-col>
    </b-row>

    <b-row v-if="!loadingState">
      <b-col>
        <b-card class="mb-3">
          <b-form @submit.stop.prevent="submit">
            <b-row>
              <b-col>
                <b-form-group id="name-group" label="Name" label-for="name">
                  <b-form-input
                    required
                    id="name"
                    v-model="name"
                    placeholder="Name"
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group
                  id="handle-group"
                  label="Handle"
                  label-for="handle"
                >
                  <input
                    required
                    id="handle"
                    v-model="handle"
                    placeholder="Handle"
                    pattern="[a-z,0-9]([a-z,0-9]|(-[a-z,0-9]))*"
                    minlength="3"
                    maxlength="64"
                    autocomplete="off"
                    class="form-control"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-form-group id="notes-group" label="Notes" label-for="notes">
              <b-form-input
                id="notes"
                v-model="notes"
                placeholder="Notes"
              ></b-form-input>
            </b-form-group>

            <b-form-group
              id="workers-group"
              label="Workers"
              label-for="workers"
            >
              <MultiSelect
                v-model="selectedWorkers"
                :options="workers"
                :match="workerMatchesSearch"
              ></MultiSelect>
            </b-form-group>

            <b-button type="submit" variant="primary">
              {{ editing ? "Update" : "Add" }} Group
            </b-button>
          </b-form>
        </b-card>
      </b-col>
    </b-row>
    <div v-else>
      <b-col class="text-center mt-5">
        <div class="mb-4">
          <b-spinner
            style="width: 4rem; height: 4rem"
            variant="primary"
            label="Loading Workers..."
          ></b-spinner>
        </div>
        <b>Loading Group Workers...</b>
      </b-col>
    </div>
  </NavigationWrapper>
</template>

<style></style>
